import React, { useState, useRef, useEffect } from "react";
import { Search, ChevronDown, Trash2, Filter } from "lucide-react";

import { SORT_OPTIONS } from "../constants";

/**
 * Search and Filters Bar Component
 */
const SearchFilters = ({ searchQuery, onSearchChange, selectedCount = 0, activeTab, onBulkDelete, sortBy, onSortChange, categories = [], categoryFilter, onCategoryFilterChange }) => {
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const sortRef = useRef(null);
  const filterRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortRef.current && !sortRef.current.contains(event.target)) {
        setShowSortDropdown(false);
      }
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentSort = SORT_OPTIONS.find((o) => o.value === sortBy) || SORT_OPTIONS[0];
  const showCategoryFilter = categories.length > 1;

  return (
    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
      {/* Search */}
      <div className="relative w-full md:w-96">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
          <Search size={18} />
        </div>
        <input
          type="text"
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full bg-app-surface border border-app-surface rounded-3xl pl-9 pr-4 py-2.5 text-[14px] text-[#FEFCEB] placeholder-app-text-secondary focus:border-[#00E7FF] outline-none transition-colors"
        />
      </div>

      {/* Right side: Sort/Filter OR Selection actions */}
      <div className="flex items-center gap-3 w-full md:w-auto">
        {selectedCount > 0 ? (
          <>
            <span className="text-sm text-app-text font-medium">
              {selectedCount} {activeTab} selected
            </span>
            <button
              onClick={onBulkDelete}
              className="flex items-center gap-2 px-4 py-2.5 bg-red-500/10 rounded-3xl text-sm text-red-400 hover:bg-red-500/20 hover:text-red-300 transition-colors"
            >
              <Trash2 size={18} />
              Delete
            </button>
          </>
        ) : (
          <div className="flex items-center gap-2 text-sm text-app-text-muted">
            <span>Sort by:</span>
            <div className="relative" ref={sortRef}>
              <button
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                className="flex items-center gap-1 text-[#FEFCEB] font-medium bg-[#082441] px-4 py-2.5 rounded-3xl hover:border-[#00E7FF] transition-colors"
              >
                {currentSort.label}
                <ChevronDown
                  size={18}
                  className={`transition-transform duration-200 shrink-0 ${showSortDropdown ? 'rotate-180' : ''}`}
                />
              </button>

              {showSortDropdown && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-[#000F1F] border border-[#192634] rounded-2xl shadow-xl z-50 overflow-hidden">
                  {SORT_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        onSortChange(option.value);
                        setShowSortDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors border-b border-[#192634] last:border-b-0 ${
                        sortBy === option.value
                          ? "text-app-accent"
                          : "text-[#FEFCEB] hover:bg-[#0D1B29]"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {showCategoryFilter && (
              <div className="relative" ref={filterRef}>
                <button
                  onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                  className="flex items-center gap-1 text-[#FEFCEB] font-medium bg-[#082441] px-4 py-2.5 rounded-3xl hover:border-[#00E7FF] transition-colors"
                >
                  <Filter size={16} />
                  {categoryFilter === "all" ? "All" : categoryFilter}
                  <ChevronDown
                    size={18}
                    className={`transition-transform duration-200 shrink-0 ${showFilterDropdown ? 'rotate-180' : ''}`}
                  />
                </button>

                {showFilterDropdown && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-[#000F1F] border border-[#192634] rounded-2xl shadow-xl z-50 overflow-hidden">
                    <button
                      onClick={() => {
                        onCategoryFilterChange("all");
                        setShowFilterDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors border-b border-[#192634] ${
                        categoryFilter === "all"
                          ? "text-app-accent"
                          : "text-[#FEFCEB] hover:bg-[#0D1B29]"
                      }`}
                    >
                      All
                    </button>
                    {categories.map((cat) => (
                      <button
                        key={cat._id}
                        onClick={() => {
                          onCategoryFilterChange(cat.label || cat.name);
                          setShowFilterDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors border-b border-[#192634] last:border-b-0 ${
                          categoryFilter === (cat.label || cat.name)
                            ? "text-app-accent"
                            : "text-[#FEFCEB] hover:bg-[#0D1B29]"
                        }`}
                      >
                        {cat.label || cat.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchFilters;
